<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center mx-2">
      {{ $t('project.members.AddMemberForm.memberFormTitle') }}
      <AppButton icon @click="cancelForm">
        <app-icon icon-name="xmark" icon-weight="fal" />
      </AppButton>
    </v-card-title>

    <v-card-subtitle class="mx-2">
      {{ $t('project.members.AddMemberForm.group', { groupName: group.name }) }}
    </v-card-subtitle>

    <v-card-text>
      <div v-if="isCurrentUserPm && isSubPayingUser" class="my-4">
        <div v-if="subscriptionTeamsPending" class="text-center">
          <AppLoader small />
        </div>
        <app-segmented-control
          v-else-if="subscriptionTeams && subscriptionTeams.length"
          :active-option="addMemberType"
          :options="memberTypeOptions"
          @change-active-option="changeMemberType"
        />
      </div>

      <form novalidate @submit.stop.prevent="submitForm" class="member-form">
        <template v-if="addMemberType === MEMBER_TYPE_OPTIONS.NEW_MEMBER">
          <i18n-t
            keypath="common.requiredFieldsInfo"
            tag="p"
            class="text-caption mb-4"
            scope="global"
          >
            <template #asterisk>
              <span class="text-error">*</span>
            </template>
          </i18n-t>
          <v-row>
            <v-col>
              <AppCombobox
                :value="memberForm.firstName"
                :items="selfContactList"
                item-value="id"
                item-title="firstName"
                return-object
                class="AddMember-known-users-input required"
                :label="$t('common.form.firstName')"
                :error-messages="firstNameErrors"
                :custom-filter="filterFunction"
                :menu-props="{ contentClass: 'AddMember-known-users-menu' }"
                @input="onFormInput($event, 'firstName')"
              >
                <template #item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template #title>
                      <AddMemberFormComboboxItem :item="item.raw"/>
                    </template>
                  </v-list-item>
                </template>
              </AppCombobox>
            </v-col>
            <v-col>
              <AppCombobox
                :value="memberForm.lastName"
                :items="selfContactList"
                :label="$t('common.form.lastName')"
                :custom-filter="filterFunction"
                item-title="lastName"
                item-value="id"
                return-object
                class="AddMember-known-users-input required"
                :menu-props="{ contentClass: 'AddMember-known-users-menu' }"
                :error-messages="lastNameErrors"
                @input="onFormInput($event, 'lastName')"
              >
                <template #item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template #title>
                      <AddMemberFormComboboxItem :item="item.raw"/>
                    </template>
                  </v-list-item>
                </template>
              </AppCombobox>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <AppCombobox
                :value="memberForm.company"
                :items="selfContactList"
                :label="$t('common.form.company')"
                :custom-filter="filterFunction"
                item-title="company"
                item-value="id"
                return-object
                class="AddMember-known-users-input"
                :menu-props="{ contentClass: 'AddMember-known-users-menu' }"
                @input="onFormInput($event, 'company')"
              >
                <template #item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template #title>
                      <AddMemberFormComboboxItem :item="item.raw"/>
                    </template>
                  </v-list-item>
                </template>
              </AppCombobox>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <AppCombobox
                :value="memberForm.email"
                :items="selfContactList"
                :label="$t('common.form.email')"
                :custom-filter="filterFunction"
                item-title="email"
                item-value="id"
                return-object
                class="AddMember-known-users-input required"
                :error-messages="emailErrors"
                :menu-props="{ contentClass: 'AddMember-known-users-menu' }"
                @input="onFormInput($event, 'email')"
              >
                <template #item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template #title>
                      <AddMemberFormComboboxItem :item="item.raw"/>
                    </template>
                  </v-list-item>
                </template>
              </AppCombobox>
            </v-col>
            <v-col>
              <AppSelect
                :model-value="memberForm.locale"
                :items="localeOptions"
                item-title="text"
                item-value="value"
                class="required"
                :label="$t('common.form.locale')"
                :error-messages="localeErrors"
                @update:model-value="onFormInput($event, 'locale')"
              >
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <v-img
                      :src="item.raw.icon"
                      :width="25"
                      class="mr-2"
                    />
                    <span>{{ item.raw.text }}</span>
                  </div>
                </template>

                <template #item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template #title>
                      <div class="d-flex align-center">
                        <v-img
                          :src="item.raw.icon"
                          :width="25"
                          class="mr-2"
                          inline
                        />
                        <span>{{ item.raw.text }}</span>
                      </div>
                    </template>
                  </v-list-item>
                </template>
              </AppSelect>
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <AddMemberFromTeam
            :model="v$"
            :memberForm="memberForm"
            @input="onFormInput($event, 'team')"
          />
        </template>

        <v-divider class="my-4" />

        <v-checkbox
          :model-value="memberForm.sendInvitationNow"
          hide-details
          density="compact"
          :label="$t('project.members.AddMemberForm.sendInvitationNow')"
          @update:model-value="onFormInput(!!$event, 'sendInvitationNow')"
        />

        <template v-if="!currentRoom.isDataroom">
          <v-checkbox
            :model-value="memberForm.askForIdCheck"
            hide-details
            density="compact"
            :label="$t('project.members.AddMemberForm.askForIdCheck')"
            @update:model-value="onFormInput(!!$event, 'askForIdCheck')"
          />
          <p class="ml-7 text-grey-700 text--x-small">
            {{ $t('project.members.AddMemberForm.askForIdCheckDetails') }}
          </p>
        </template>

        <v-checkbox
          v-if="isProjectManager"
          :model-value="memberForm.projectManagersAreHidden"
          hide-details
          density="compact"
          :label="$t('project.members.AddMemberForm.hideProjectManager')"
          @update:model-value="onFormInput(!!$event, 'projectManagersAreHidden')"
        />

        <div class="d-flex align-center">
          <v-switch
            :model-value="memberForm.withCustomInvitationMessage"
            :label="$t('project.members.AddMemberForm.addCustomInvitationMessage')"
            :disabled="!memberForm.sendInvitationNow"
            hide-details
            @update:model-value="onFormInput(!!$event, 'withCustomInvitationMessage')"
          />
          <AppButton
            text
            class="mx-2 text-decoration-underline"
            :disabled="!memberForm.sendInvitationNow"
            @click="previewInvitation"
          >
            {{ $t('project.members.AddMemberForm.previewLink') }}
          </AppButton>
        </div>

        <VisualTextEditor
          v-if="memberForm.withCustomInvitationMessage"
          :value="memberForm.customInvitationMessage"
          :error-messages="customMessageErrors"
          :max-length="customMessageMaxLength"
          counter="footer"
          @blur="v$.memberForm.customInvitationMessage.$touch"
          @input="onFormInput($event, 'customInvitationMessage')"
        />

        <input type="submit" class="d-none">
      </form>
    </v-card-text>

    <v-card-actions class="ma-2">
      <v-spacer/>
      <AppButton @click="cancelForm">
        {{ $t('common.cancel') }}
      </AppButton>
      <AppButton
        color="primary"
        :loading="okLoading"
        @click="submitForm"
      >
        {{ $t('common.add') }}
      </AppButton>
    </v-card-actions>
  </v-card>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapState, mapGetters } from 'vuex'

import AppCombobox from '@/common/AppCombobox.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppSelect from '@/common/AppSelect.vue'
import { countryFlags } from '@/common/assets'
import AppButton from '@/common/buttons/AppButton.vue'
import { formFieldErrors } from '@/common/utils/form'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { GET_SUBSCRIPTION_TEAMS } from '@/store/modules/subscription/action_types'
import { GET_SELF_CONTACT_LIST } from '@/store/modules/user/action_types'
import { postmarkEmail } from '@/validators/postmarkEmail'

import AddMemberFormComboboxItem from './AddMemberFormComboboxItem.vue'
import AddMemberFromTeam from './AddMemberFromTeam.vue'
import { MEMBER_TYPE_OPTIONS } from './types.ts'

const CUSTOM_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'AddMemberForm',
  components: {
    AddMemberFromTeam,
    AppLoader,
    AddMemberFormComboboxItem,
    AppButton,
    AppCombobox,
    AppSelect,
    VisualTextEditor,
  },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    group: {
      type: Object,
      required: true,
    },
    memberForm: {
      type: Object,
      required: true,
    },
    okLoading: {
      type: Boolean,
      default: false,
    },
  },
  validations () {
    if (this.addMemberType === MEMBER_TYPE_OPTIONS.NEW_MEMBER) {
      return {
        memberForm: {
          firstName: { required },
          lastName: { required },
          email: {
            required,
            postmarkEmail,
          },
          locale: { required },
          customInvitationMessage: {
            maxLength: () => !this.memberForm.withCustomInvitationMessage || this.customMessageIsTooLong,
          },
        },
      }
    } else {
      return {
        memberForm: {
          team: {
            required,
          },
          customInvitationMessage: {
            maxLength: () => !this.memberForm.withCustomInvitationMessage || this.customMessageIsTooLong,
          },
        },
      }
    }
  },
  data () {
    return {
      MEMBER_TYPE_OPTIONS,
      addMemberType: MEMBER_TYPE_OPTIONS.NEW_MEMBER,
      customMessageMaxLength: CUSTOM_MESSAGE_MAX_LENGTH,
      localeOptions: [
        {
          text: this.$t('project.members.AddMemberForm.locale.french'),
          value: 'fr',
          icon: countryFlags.fr,
        },
        {
          text: this.$t('project.members.AddMemberForm.locale.english'),
          value: 'en',
          icon: countryFlags.en,
        },
        {
          text: this.$t('project.members.AddMemberForm.locale.german'),
          value: 'de',
          icon: countryFlags.de,
        },
      ],
    }
  },
  computed: {
    ...mapState('user', ['selfContactList']),
    ...mapState('room', ['currentRoom']),
    ...mapState('subscription', ['subscriptionTeamsPending', 'subscriptionTeams']),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapGetters('user', ['isSubPayingUser']),
    isProjectManager () {
      return this.group.isPm
    },
    firstNameErrors () {
      return formFieldErrors(
          this.v$.memberForm.firstName,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.firstName') })],
          ],
      )
    },
    lastNameErrors () {
      return formFieldErrors(
          this.v$.memberForm.lastName,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.lastName') })],
          ],
      )
    },
    localeErrors () {
      return formFieldErrors(
          this.v$.memberForm.locale,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.locale') })],
          ],
      )
    },
    emailErrors () {
      return formFieldErrors(
          this.v$.memberForm.email,
          [
            ['postmarkEmail', this.$t('common.validations.email')],
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('common.form.email') })],
          ],
      )
    },
    customMessageErrors () {
      return formFieldErrors(
          this.v$.memberForm.customInvitationMessage,
          [
            ['maxLength', this.$t('common.validations.textTooLong')],
          ],
      )
    },
    customMessageLength () {
      return escapeHtml(this.memberForm.customInvitationMessage).length
    },
    customMessageIsTooLong () {
      return this.customMessageMaxLength > this.customMessageLength
    },
    memberTypeOptions () {
      return Object.values(MEMBER_TYPE_OPTIONS).map(option => {
        return {
          name: this.$t(`project.members.memberTypesOptions.${option}`),
          value: option,
        }
      })
    },
  },
  created () {
    if (this.isCurrentUserPm && this.isSubPayingUser) {
      this.getSubscriptionTeams()
    }

    if (this.currentRoom.isDataroom) {
      this.memberForm.askForIdCheck = false
    }
  },
  async mounted () {
    await this.GET_SELF_CONTACT_LIST()
  },
  methods: {
    ...mapActions('user', [GET_SELF_CONTACT_LIST]),
    ...mapActions('subscription', [GET_SUBSCRIPTION_TEAMS]),
    async getSubscriptionTeams () {
      try {
        await this.GET_SUBSCRIPTION_TEAMS()
      } catch (error) {
        toast.error(this.$t('subscriptions.views.SubscriptionTeams.getTeamsError'))
      }
    },
    filterFunction (_, queryText, item) {
      return item.raw.firstName?.toLowerCase().includes(queryText.toLowerCase()) ||
        item.raw.lastName?.toLowerCase().includes(queryText.toLowerCase()) ||
        item.raw.company?.toLowerCase().includes(queryText.toLowerCase()) ||
        item.raw.email?.toLowerCase().includes(queryText.toLowerCase())
    },
    onFormInput (value, property) {
      switch (typeof value) {
        case 'string': {
          this.$emit('update:memberForm', {
            ...this.memberForm,
            [property]: value.trim(),
          })
          break
        }
        case 'boolean': {
          this.$emit('update:memberForm', {
            ...this.memberForm,
            [property]: value,
          })
          break
        }
        case 'object': {
          if (value) {
            if (this.addMemberType === MEMBER_TYPE_OPTIONS.NEW_MEMBER) {
              this.$emit('update:memberForm', {
                ...this.memberForm,
                firstName: value.firstName?.trim(),
                lastName: value.lastName?.trim(),
                email: value.email?.trim(),
                company: value.company?.trim(),
                locale: value.locale?.trim(),
              })
            } else if (value.id) {
              this.$emit('update:memberForm', {
                ...this.memberForm,
                team: value,
              })
            }
          }
          break
        }
      }
    },
    changeMemberType (value) {
      this.addMemberType = value
      this.$emit('change-add-member-type', value)
    },
    previewInvitation () {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        this.$emit('previewInvitation')
      }
    },
    cancelForm () {
      this.$emit('onCancel')
    },
    submitForm () {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        // We wait for all potential input events to be processed
        // An immediate timeout shares the same stack as the events,
        // so it's processed _after_ the events already scheduled to be handled
        setTimeout(() => {
          this.$emit('onValidate')
        }, 0)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.v-dialog {
  .v-card-subtitle {
    opacity: 1;
    font-weight: bold;
  }

  .v-card-text {
    padding-block: 0 !important;
  }
}

.member-form {
  .v-row + .v-row {
    margin-top: 4px;
  }
}

.AddMember-known-users-menu {
  .v-list-item {
    border-bottom: 1px solid #eee;
    padding-top: 6px;
    padding-bottom: 6px !important;
  }
}

.AddMember-known-users-input {
  :deep(.v-field__append-inner) {
    display: none;
  }

  :deep(.v-input__details) {
    padding-inline: 0;
  }
}
</style>
